import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFormMutations } from "@/hooks/use-form-mutations";
import { getForms } from "@/lib/forms-api";
import { ManifestSlot } from "@/components/forms/ManifestSlot";
import { FormCard } from "@/components/forms/FormCard";
import { CreateFormDialog } from "@/components/forms/CreateFormDialog";
import type { Form, FormType } from "@/types/forms";

export function FormList() {
  const { orgId, slug } = useParams<{ orgId: string; slug: string }>();
  const navigate = useNavigate();
  const { create, setStatus } = useFormMutations();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("active");

  // Manifests — exactly one active per type
  const { data: sodData, isLoading: sodLoading } = useQuery({
    queryKey: ["forms", orgId, { type: "manifest_sod", isArchived: false }],
    queryFn: () => getForms({ type: "manifest_sod", isArchived: false }),
    enabled: !!orgId,
  });
  const { data: eodData, isLoading: eodLoading } = useQuery({
    queryKey: ["forms", orgId, { type: "manifest_eod", isArchived: false }],
    queryFn: () => getForms({ type: "manifest_eod", isArchived: false }),
    enabled: !!orgId,
  });

  // Standard forms — active (not archived)
  const { data: stdActiveData, isLoading: stdActiveLoading } = useQuery({
    queryKey: ["forms", orgId, { type: "standard", isArchived: false }],
    queryFn: () => getForms({ type: "standard", isArchived: false }),
    enabled: !!orgId,
  });

  // Standard forms — archived (lazy, only when tab is selected)
  const { data: stdArchivedData } = useQuery({
    queryKey: ["forms", orgId, { type: "standard", isArchived: true }],
    queryFn: () => getForms({ type: "standard", isArchived: true }),
    enabled: !!orgId && activeTab === "archived",
  });

  const loading = sodLoading || eodLoading || stdActiveLoading;

  const sodForm = (sodData?.data ?? [])[0] ?? null;
  const eodForm = (eodData?.data ?? [])[0] ?? null;
  const activeForms = stdActiveData?.data ?? [];
  const archivedForms = stdArchivedData?.data ?? [];

  const base = `/${orgId}/${slug}/settings/forms`;

  async function handleCreateManifest(type: FormType) {
    const title = type === "manifest_sod" ? "Start of Day" : "End of Day";
    try {
      const form = await create.mutateAsync({ title, type });
      navigate(`${base}/${form.id}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create manifest");
    }
  }

  async function handleCreateStandard(data: { title: string; description?: string }) {
    try {
      const form = await create.mutateAsync({ ...data, type: "standard" });
      navigate(`${base}/${form.id}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create form");
    }
  }

  function handleEdit(form: Form) {
    navigate(`${base}/${form.id}`);
  }

  async function handlePublish(form: Form) {
    try {
      await setStatus.mutateAsync({ id: form.id, status: "published" });
      toast.success("Form published");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to publish form");
    }
  }

  async function handleUnpublish(form: Form) {
    try {
      await setStatus.mutateAsync({ id: form.id, status: "draft" });
      toast.success("Form unpublished");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to unpublish form");
    }
  }

  async function handleArchive(form: Form) {
    try {
      await setStatus.mutateAsync({ id: form.id, isArchived: true });
      toast.success("Form archived");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to archive form");
    }
  }

  async function handleRestore(form: Form) {
    try {
      await setStatus.mutateAsync({ id: form.id, isArchived: false });
      toast.success("Form restored");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to restore form");
    }
  }

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading forms...</p>;
  }

  return (
    <div className="space-y-8">
      {/* Manifests */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Manifests</h2>
        <div className="space-y-2">
          <ManifestSlot
            type="sod"
            form={sodForm}
            loading={create.isPending}
            onCreateClick={handleCreateManifest}
            onEditClick={handleEdit}
            onPublishClick={handlePublish}
            onUnpublishClick={handleUnpublish}
            onArchiveClick={handleArchive}
          />
          <ManifestSlot
            type="eod"
            form={eodForm}
            loading={create.isPending}
            onCreateClick={handleCreateManifest}
            onEditClick={handleEdit}
            onPublishClick={handlePublish}
            onUnpublishClick={handleUnpublish}
            onArchiveClick={handleArchive}
          />
        </div>
      </section>

      {/* Standard Forms */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Standard Forms</h2>
          <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create
          </Button>
        </div>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="active">
              Active{activeForms.length > 0 && ` (${activeForms.length})`}
            </TabsTrigger>
            <TabsTrigger value="archived">
              Archived{archivedForms.length > 0 && ` (${archivedForms.length})`}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="active">
            {activeForms.length > 0 ? (
              <div className="space-y-2">
                {activeForms.map((form) => (
                  <FormCard
                    key={form.id}
                    form={form}
                    onEditClick={handleEdit}
                    onPublishClick={handlePublish}
                    onUnpublishClick={handleUnpublish}
                    onArchiveClick={handleArchive}
                    onRestoreClick={handleRestore}
                  />
                ))}
              </div>
            ) : (
              <p className="py-4 text-sm text-muted-foreground">
                No forms yet. Create one to get started with clock-out workflows.
              </p>
            )}
          </TabsContent>
          <TabsContent value="archived">
            {archivedForms.length > 0 ? (
              <div className="space-y-2">
                {archivedForms.map((form) => (
                  <FormCard
                    key={form.id}
                    form={form}
                    onEditClick={handleEdit}
                    onPublishClick={handlePublish}
                    onUnpublishClick={handleUnpublish}
                    onArchiveClick={handleArchive}
                    onRestoreClick={handleRestore}
                  />
                ))}
              </div>
            ) : (
              <p className="py-4 text-sm text-muted-foreground">
                No archived forms.
              </p>
            )}
          </TabsContent>
        </Tabs>
      </section>

      <CreateFormDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSubmit={handleCreateStandard}
      />
    </div>
  );
}
